import './polygon-condition.css';

import h from 'hyperscript';
import polygonSelectionIcon from '@disy/cadenza-icons/polygon-selection.svg';

import { Select } from 'ui/select/select';

import { ConditionElement } from 'cadenza/conditions/condition-element';
import { hashCode } from 'cadenza/utils/hash-code';
import { logger } from 'cadenza/utils/logging';
import { on, unByKey } from 'cadenza/utils/event-util';
import { icon } from 'cadenza/utils/icon/icon';
import { errorAlert } from 'cadenza/alert';
import { MapBasedConditionApi } from 'cadenza/api-client/conditions/map-based-condition-api';

import i18n from './messages.properties';

const NAME = 'd-polygon-condition';
export class PolygonCondition extends ConditionElement {

  #select;
  #indexMap = new Map();
  #indexCounter = 1;
  #latestSetValuesPromise; // for keeping consistency for other conditions, see onConditionChange

  connectedCallback () {
    this.classList.add(NAME);
    const data = this.previousElementSibling.value;
    this._config = data.configuration;
    this.conditionApi = new MapBasedConditionApi({
      conditionSetId: this.conditionSetId,
      conditionName: this.conditionName,
      conditionId: this.conditionId,
      conditionValuesSetHashGetter: () => this.conditionValuesSetHash
    });

    const elementRight = h('button.dialog-trigger.button.button-icon.button-borderless', {
      title: i18n('dialog-trigger.title'),
      onclick: event => {
        event.preventDefault();
        this._showDialog();
      }
    }, icon(polygonSelectionIcon));

    this.#select = new Select({
      elementRight,
      removeOnly: true,
      multiple: true
    });
    this.#select.addEventListener('change', () => {
      if (this._resetHighlighting) {
        this._resetHighlighting();
      }
      this.onConditionChange();
    });

    this.append(this.#select);

    this.defaultValue = [];

    if (!this.value.length) {
      this.value = data.value ? data.value.map(([ geometry, text, srs ]) => {
        this._addToIndexMap(String(hashCode(JSON.stringify(geometry))), text);
        return ({ geometry, text, srs });
      }) : [];
    }
    this.initChanged();

    if (this.filterController?.layerId) { // not available in workbooks
      import(/* webpackChunkName: "geometry-highlighter" */ './geometry-highlighter').then(({ highlightGeometry, resetHighlighting }) => {
        this._highlightGeometry = highlightGeometry;
        this._resetHighlighting = resetHighlighting;
        this._eventKeys = [
          on(this, 'mouseover', event => this._onMouseover(event)),
          on(this, 'mouseleave', () => this._onMouseleave(event))
        ];
      }).catch(error => {
        logger.error('Failed to import module geometry-highlighter.', error);
      });
    }
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
  }

  get isCustom () {
    return this.getAttribute('is-custom') === 'true';
  }

  _addToIndexMap (id, text) {
    // init indexMap (extract index from text and insert it into the map)
    // text has the formate '`polygonType` `index`'
    let index = Number(text.split(' ').pop());
    if (isNaN(index)) {
      index = this.#indexCounter;
      this.#indexCounter++;
    } else if (index >= this.#indexCounter) {
      this.#indexCounter = index + 1;
    }
    this.#indexMap.set(id, index);
  }

  _getIndex (id) {
    const map = this.#indexMap;
    if (!map.has(id)) {
      map.set(id, this.#indexCounter++);
    }
    return map.get(id);
  }

  set value (rows) {
    this.#select.selection = rows.map(({ geometry, text, srs }) => {
      const id = String(hashCode(JSON.stringify(geometry)));
      return ({
        id,
        text: text || `${this._config.polygonType} ${this._getIndex(id)}`,
        geometry,
        srs
      });
    });
  }

  get value () {
    return this.#select.selection.map(({ id, text, geometry, srs }) => ({
      id,
      geometry,
      text,
      srs
    }));
  }

  get numberOfConditionValueRows () {
    return this.value.length;
  }

  onConditionChange () {
    // the filter controller is not availabe in workbooks
    this.filterController?.disableOtherConditionsIfNeededForConsistency([ this ]);
    const rows = this.value.map(({ geometry, text, srs }) => [ geometry, text, srs ]);
    const setValuesPromise = this.conditionApi.setValue(rows);
    this.#latestSetValuesPromise = setValuesPromise;
    setValuesPromise
      .then((json) => super.onConditionChange(json))
      .catch(error => this.handleConditionValueChangeError(error))
      .finally(() => {
        // if the changes are not fully processed yet, disable the other conditions again.
        const enable = setValuesPromise === this.#latestSetValuesPromise;
        // the filter controller is not availabe in workbooks
        this.filterController?.setConditionsDisabledForConsistency(enable, this);
      });
  }

  _onMouseover (event) {
    const li = event.target.closest('li');
    if (li) {
      // innerText should be used here in order to get just the text without new line characters
      // eslint-disable-next-line no-restricted-syntax
      const data = li.closest('d-select').selection.find(el => el.text === li.innerText);
      if (data) {
        this._highlightGeometry(data.highlightingGeometry || data.geometry, data.text, data.srs);
      }
    }
  }

  _onMouseleave () {
    this._resetHighlighting();
  }

  _showDialog () {
    return this.conditionApi.registerConditionMap()
      .then(mapId => {
        this._mapId = mapId;
        return import(/* webpackChunkName: "polygon-condition-modal" */ './polygon-condition-modal');
      })
      .then(({ PolygonConditionModal }) => {
        const dialog = new PolygonConditionModal({
          icon: icon(polygonSelectionIcon),
          title: this.conditionLabel,
          mapId: this._mapId,
          conditionSetId: this.conditionSetId,
          conditionId: this.conditionId,
          conditionValuesSetHash: this.conditionValuesSetHash,
          maxSelection: this.maxSelection,
          selectedGeometries: this.value.map(({ geometry, srs }) => ({
            geometry,
            srs
          }))
        });
        dialog.addEventListener('close', () => {
          this.conditionApi.unregisterConditionMap(this._mapId);
          dialog.remove();
          if (dialog.returnValue) {
            this.value = dialog.returnValue.map(({ geometry, srs }) => ({
              geometry,
              srs
            }));
            this.onConditionChange();
          }
        });
        document.body.appendChild(dialog);
        dialog.showModal();
      })
      .catch(() => {
        errorAlert(i18n('error.dialog.title'), i18n('error.dialog.message'));
        this.conditionApi.unregisterConditionMap(this._mapId);
      });
  }

}

customElements.define(NAME, PolygonCondition);
